import { getLocale, getTranslations } from "next-intl/server"

import { HeaderContentWithScrollClient } from "./HeaderContentWithScrollClient"
import { generateMenuData } from "./utils/generateMenuData"

import "./index.scss"

import { getAntdLocale } from "@repo/shared"
import { ConfigProvider } from "antd"

import { IHeaderCoreProps, UtilityNavItem } from "../../interface"
import { AnnouncementBanner } from "./AnnouncementBanner"
import { TopUtilityBar } from "./components"

const TOP_UTILITY_HEIGHT = 41
const MAIN_NAV_HEIGHT_DESKTOP = 64
const MAIN_NAV_HEIGHT_MOBILE = 48
const ANNOUNCEMENT_HEIGHT_DESKTOP = 38
const ANNOUNCEMENT_HEIGHT_MOBILE = 36

const buildUtilityNav = (locale: string, t: (key: string) => string): UtilityNavItem[] => {
  const getPrefix = (key: string) => {
    return `/${locale}${key}`
  }

  return [
    { label: t("utilityNav.Announcements"), href: `${getPrefix("/announcements")}`, type: "link" },
    { label: t("utilityNav.AboutUs"), href: `${getPrefix("/about-us")}`, type: "link" },
    { label: t("utilityNav.Careers"), href: `${getPrefix("/careers")}`, type: "link" },
    { label: t("utilityNav.DownloadAPP"), href: `${getPrefix("/app")}`, type: "link" },
  ]
}

/**
 * 获取字体配置（服务端处理）
 */
function getFontFamily(locale: string): string {
  const fontFamilyMap: Record<string, string> = {
    th: "var(--font-noto-sans-thai), var(--font-montserrat), var(--font-roboto), Arial, Helvetica, sans-serif",
  }
  return fontFamilyMap[locale] ?? "var(--font-montserrat), var(--font-roboto), Arial, Helvetica, sans-serif"
}

/**
 * 生成样式注入（服务端处理）
 */
function generateStyleInject(
  transparentMode: boolean,
  initialHeaderMarginTopDesktop: number,
  initialHeaderMarginTopMobile: number
): React.ReactNode {
  if (transparentMode) {
    return null
  }

  return (
    <style>{`
      :root {
        --header-margin-top-desktop: ${initialHeaderMarginTopDesktop}px;
        --header-margin-top-mobile: ${initialHeaderMarginTopMobile}px;
      }
      main { margin-top: var(--header-margin-top-desktop); }
      @media (max-width: 768px) {
        main { margin-top: var(--header-margin-top-mobile); }
      }
    `}</style>
  )
}

/**
 * 注入 --announcement-height（仅服务端注入，避免抖动）。
 * 该变量当前用于移动端 Publicity 的 margin-top。
 * 有公告时为 84px（公告条 36 + 主导航 48），无公告时为 48px。
 */
function getAnnouncementHeightStyle(hasAnnouncement: boolean): React.ReactNode {
  const height = hasAnnouncement ? ANNOUNCEMENT_HEIGHT_MOBILE + MAIN_NAV_HEIGHT_MOBILE : MAIN_NAV_HEIGHT_MOBILE
  return (
    <style>{`
      :root {
        --announcement-height: ${height}px;
      }
    `}</style>
  )
}

/**
 * 服务端组件：准备菜单与导航数据，减少客户端计算
 */
export async function HeaderContentWithScroll(props: IHeaderCoreProps) {
  const locale = await getLocale()
  const t = await getTranslations("HeaderComp")

  const menuItems = generateMenuData(props.markets, locale, t)
  const utilityNav = buildUtilityNav(locale, t)
  const isTransparentMode = props.transparentMode !== false
  const hasAnnouncement = props.showAnnouncementBanner !== false && (props.announcements?.length ?? 0) > 0

  // 预估高度（桌面包含顶部工具栏，移动端不包含）
  const desktopHeaderMarginTop = isTransparentMode
    ? 0
    : TOP_UTILITY_HEIGHT + MAIN_NAV_HEIGHT_DESKTOP + (hasAnnouncement ? ANNOUNCEMENT_HEIGHT_DESKTOP : 0)
  const mobileHeaderMarginTop = isTransparentMode
    ? 0
    : MAIN_NAV_HEIGHT_MOBILE + (hasAnnouncement ? ANNOUNCEMENT_HEIGHT_MOBILE : 0)

  // 服务端处理：Ant Design 配置
  const antdLocale = getAntdLocale(locale)
  const fontFamily = getFontFamily(locale)

  // 服务端处理：样式注入
  const styleInject = generateStyleInject(isTransparentMode, desktopHeaderMarginTop, mobileHeaderMarginTop)

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        token: {
          fontFamily,
          colorPrimary: "#d7000f",
          borderRadius: 2,
        },
      }}
    >
      {styleInject}
      {getAnnouncementHeightStyle(hasAnnouncement)}
      <HeaderContentWithScrollClient
        className={props.className}
        transparentMode={props.transparentMode}
        logo={props.logo}
        menuItems={menuItems}
        userInfo={props.userInfo ?? null}
        initialHeaderMarginTopDesktop={desktopHeaderMarginTop}
        initialHeaderMarginTopMobile={mobileHeaderMarginTop}
      >
        <AnnouncementBanner announcements={hasAnnouncement ? (props.announcements ?? []) : []} />
        <TopUtilityBar utilityNav={utilityNav} userInfo={props.userInfo ?? null} />
      </HeaderContentWithScrollClient>
    </ConfigProvider>
  )
}
